<template>
  <div>
    <input
      ref="fileinput"
      type="file"
      :disabled="disabled"
      :accept="accept || '*.*'"
      class="hidden"
      @change="onChange()"
    />
    <div v-show="!hasFile && !selectedFileLink" class="flex h-full flex-col">
      <div
        ref="dopZone"
        class="box-border h-full border-2 border-dashed border-neutral-400 py-4 px-8"
        :class="{
          'border-emerald-500': !disabled && isOverDropZone,
          'bg-emerald-100': !disabled && isOverDropZone,
          'bg-neutral-100': disabled
        }"
      >
        <div class="flex flex-col items-center h-full justify-evenly text-neutral-500 dark:text-neutral-300 uppercase gap-4">
          <div v-html="$t('input.dragAndDrop')"></div>
          <div>
            <pe-button type="primary" size="large" :disabled="disabled" class="uppercase" @click="fileinput.click()">{{ t('labels.clickHere') }}</pe-button>
          </div>
        </div>
      </div>

      <slot name="extra" />
    </div>

    <div v-show="hasFile || !!selectedFileLink" class="text-center">
      <slot name="hasFile" :scope="{ fileName, imgData, removeFile, changeFile }">
        <div ref="previewImg" class="block h-40 w-full">
          <img :src="imgData" :alt="fileName" class="h-full w-full object-contain" />
        </div>
        <div class="my-4 text-xl">{{ fileName }}</div>
        <div>
          <pe-button type="danger" size="large" @click="removeFile()">{{ t('button.delete', {text: t('labels.file').toLowerCase()}) }}</pe-button>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { useDropZone } from '@vueuse/core'
import { toast, validateFileSize } from '@/common/util'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  disabled?: boolean
  allowMultiple?: boolean // @TODO: Implementar regra
  selectedFileLink?: string
  accept?: string
  validateType?: string[]
  maxFileSize?: number
  unitSize?: 'KB' | 'MB'
}>()

const emit = defineEmits(['onChange'])

const fileinput = ref<HTMLInputElement>()
const dopZone = ref<HTMLDivElement>()
const imgData = ref('')

const fileName = ref('')
const hasFile = ref(false)

watch(
  () => props.selectedFileLink,
  (link) => {
    if (link) {
      imgData.value = link
      fileName.value = link.split('/').pop()
    } else {
      imgData.value = ''
      fileName.value = ''
    }

    nextTick(() => (hasFile.value = !!link))
  }
)

const removeFile = () => {
  fileinput.value.value = null
  onChange()
}

const changeFile = () => {
  fileinput.value.click()
}

const readFile = (file: File | null) => {
  if (file) {
    hasFile.value = true
    fileName.value = file.name

    if (file.type.startsWith('image/')) {
      const reader = new FileReader()

      reader.readAsDataURL(file)
      reader.onload = () => {
        imgData.value = `${reader.result}`
      }
    }

    // @TODO: Da pra adicionar um else aqui pra colocar uma imagem de feedback caso seja: Documentos, arquivos (binarios), textos, musica, video, etc...
  } else {
    hasFile.value = false
    fileName.value = ''
  }
}

const onChange = () => {
  const file = fileinput.value?.files[0] || null

  if(
    file && props?.validateType && 
    !props.validateType.includes(file.type.split('/')[1])
  ) return toast.error({title: t('toasts.invalidFile'), message: t('toasts.acceptFiles', [props.validateType.join(', ')])})


  if((props.maxFileSize && props.unitSize) && file && !validateFileSize(file.size, props.maxFileSize, props.unitSize)) return false

  readFile(file)
  emit('onChange', fileinput.value?.files)
}

const onDrop = (files: File[] | null) => {

  if((props.maxFileSize && props.unitSize) && files[0] && !validateFileSize(files[0].size, props.maxFileSize, props.unitSize)) return false

  readFile(files[0])
  emit('onChange', files)
}

const { isOverDropZone } = useDropZone(dopZone, onDrop)
</script>

<style lang="scss" scoped></style>
